import {
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Progress,
  Stack,
  Flex,
  Box,
  Tooltip,
  Circle,
} from '@chakra-ui/react';
import { HelivanText } from '../helivan/HelivanText';
import { useState, useEffect, useRef } from 'react';
import {
  ProjectDocument,
  ProjectQuestion,
  ProjectSurveyResponseInput,
  ProjectSurveyResponseType,
  useAddYourselfMutation,
} from '../../generated/graphql';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanSurveyQuestion } from '../helivan/HelivanSurveyQuestion';
import { HelivanColor } from '../helivan/HelivanColor';

interface AddYourselfModalProps {
  isOpen: boolean;
  onClose: () => void;
  questions: ProjectQuestion[]; // topic is key, questions are values
  projectTitle: string;
  projectId: string;
}

export const AddYourselfModal = ({
  isOpen,
  onClose,
  questions,
  projectTitle,
  projectId,
}: AddYourselfModalProps) => {
  const [addYourself, { loading: addYourselfLoading }] =
    useAddYourselfMutation();
  const questionsRecord: Record<string, string[]> = {};
  questions.forEach((question) => {
    if (!questionsRecord[question.topic]) {
      questionsRecord[question.topic] = [];
    }
    questionsRecord[question.topic].push(question.question);
  });

  // Store responses with question as key and response as value
  const [responses, setResponses] = useState<
    Record<string, ProjectSurveyResponseType>
  >({});
  const [step, setStep] = useState(0);
  const topics = Object.keys(questionsRecord);
  const curQuestions =
    step === 0 || step === topics.length + 1
      ? []
      : Object.values(questionsRecord)[step - 1];
  const totalSteps = topics.length;
  const progress = ((step - 0.1) / totalSteps) * 100;

  // Check if all current questions have been answered
  const allCurrentQuestionsAnswered = curQuestions.every(
    (question) => responses[question] !== undefined
  );

  const handleSubmit = () => {
    const responsesArray: ProjectSurveyResponseInput[] = Object.entries(
      responses
    ).map(([question, response]) => {
      const topic = Object.keys(questionsRecord).find((topic) =>
        questionsRecord[topic].includes(question)
      );
      if (!topic) {
        throw new Error('Topic not found');
      }
      return {
        question,
        topic,
        response,
      };
    });

    addYourself({
      variables: {
        input: {
          projectId,
          responses: responsesArray,
        },
      },
      refetchQueries: [
        { query: ProjectDocument, variables: { id: projectId } },
      ],
      onCompleted: () => {
        setStep(step + 1);
      },
    });
  };

  const handleClose = () => {
    setStep(0);
    setResponses({});
    document.querySelector('.chakra-modal__body')?.scrollTo(0, 0);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size={step === 0 || step === topics.length + 1 ? 'lg' : '4xl'}
    >
      <ModalOverlay />
      <ModalContent maxH="90vh">
        {step !== 0 && step !== topics.length + 1 && (
          <Flex p={6} pb={2} direction="column" width="full">
            <Box position="relative" mb={4} mt={4}>
              <Progress
                value={progress}
                size="sm"
                colorScheme="green"
                borderRadius="md"
                height="8px"
              />

              {/* Notches for each topic */}
              <Flex
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                justify="space-between"
                align="center"
                px={1}
              >
                {topics.map((topic, idx) => {
                  // Calculate position as percentage
                  const position = (idx / (totalSteps - 1)) * 100;
                  const isCurrentTopic = idx === step - 1;
                  return (
                    <Tooltip key={idx} label={topic} placement="top">
                      <Box
                        position={
                          idx === 0 || idx === totalSteps - 1
                            ? 'relative'
                            : 'absolute'
                        }
                        left={
                          idx !== 0 && idx !== totalSteps - 1
                            ? `${position}%`
                            : undefined
                        }
                        transform={
                          idx !== 0 && idx !== totalSteps - 1
                            ? 'translateX(-50%)'
                            : undefined
                        }
                        width={isCurrentTopic ? '32px' : '24px'}
                        height={isCurrentTopic ? '32px' : '24px'}
                        borderRadius="full"
                        bg={idx <= step - 1 ? 'green.500' : 'gray.200'}
                        border="2px solid white"
                        cursor="pointer"
                        transition="width 0.2s, height 0.2s"
                      />
                    </Tooltip>
                  );
                })}
              </Flex>
            </Box>
            <ModalCloseButton onClick={handleClose} />
          </Flex>
        )}

        <ModalBody overflowY="auto">
          {step === 0 ? (
            <Flex width="full" justifyContent="center">
              <Stack spacing={3} alignItems="center" maxWidth="300px" p={2}>
                <HelivanText variant="pageHeader">Add yourself</HelivanText>
                <HelivanText
                  textAlign="center"
                  variant="largeBody"
                  fontWeightOverride={400}
                >
                  This survey will help us understand your stances on important
                  issues relating to {projectTitle}.
                </HelivanText>
                <HelivanText
                  variant="largeBody"
                  fontWeightOverride={400}
                  pt={4}
                >
                  Estimated total time: 7 minutes.
                </HelivanText>
                <HelivanButton
                  variant="primary"
                  onClick={() => setStep(step + 1)}
                >
                  Begin
                </HelivanButton>
              </Stack>
            </Flex>
          ) : step === topics.length + 1 ? (
            <Flex width="full" justifyContent="center" height="full">
              <Stack spacing={3} maxWidth="400px" alignItems="center" pt={6}>
                <AnimatedCheckmark />
                <HelivanText variant="pageHeader" textAlign="center">
                  Thank you for your responses!
                </HelivanText>
                <HelivanText variant="largeBody" textAlign="center">
                  Your perspective should show up shortly.
                </HelivanText>
                <HelivanButton variant="primary" onClick={handleClose} mt={4}>
                  Close
                </HelivanButton>
              </Stack>
            </Flex>
          ) : (
            <QuestionList
              topic={topics[step - 1]}
              questions={curQuestions}
              responses={responses}
              onResponseChange={(question, response) => {
                setResponses((prev) => ({
                  ...prev,
                  [question]: response,
                }));
              }}
            />
          )}
        </ModalBody>

        <ModalFooter>
          {step !== 0 && step !== topics.length + 1 && (
            <HStack spacing={2}>
              <HelivanButton
                variant="outline"
                onClick={() => {
                  setStep(step - 1);
                  document.querySelector('.chakra-modal__body')?.scrollTo(0, 0);
                }}
                isDisabled={step === 0}
              >
                Back
              </HelivanButton>
              <HelivanButton
                variant="primary"
                isLoading={addYourselfLoading}
                onClick={() => {
                  if (step === Object.keys(questionsRecord).length) {
                    handleSubmit();
                  } else {
                    setStep(step + 1);
                    document
                      .querySelector('.chakra-modal__body')
                      ?.scrollTo(0, 0);
                  }
                }}
                isDisabled={!allCurrentQuestionsAnswered}
              >
                {step === Object.keys(questionsRecord).length
                  ? 'Submit'
                  : 'Next'}
              </HelivanButton>
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const QuestionList = ({
  topic,
  questions,
  responses,
  onResponseChange,
}: {
  topic: string;
  questions: string[];
  responses: Record<string, ProjectSurveyResponseType>;
  onResponseChange: (
    question: string,
    response: ProjectSurveyResponseType
  ) => void;
}) => {
  const [answeredIndex, setAnsweredIndex] = useState(-1);
  const questionRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Initialize refs array
  useEffect(() => {
    questionRefs.current = questionRefs.current.slice(0, questions.length);
  }, [questions]);

  // Scroll to next question when answeredIndex changes
  useEffect(() => {
    if (answeredIndex >= 0 && answeredIndex < questions.length - 1) {
      const nextQuestionElement = questionRefs.current[answeredIndex + 1];
      if (nextQuestionElement) {
        nextQuestionElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [answeredIndex, questions.length]);

  return (
    <Stack spacing={1} alignItems="center">
      <HelivanText variant="sectionHeader">{topic}</HelivanText>
      <Stack spacing={1}>
        {questions.map((question, index) => {
          return (
            <div
              key={question}
              ref={(el) => {
                questionRefs.current[index] = el;
              }}
            >
              <HelivanSurveyQuestion
                question={question}
                initialResponse={responses[question]}
                onResponseChange={(response) => {
                  setAnsweredIndex(index);
                  onResponseChange(question, response);
                }}
              />
            </div>
          );
        })}
      </Stack>
    </Stack>
  );
};

// Animated checkmark component
const AnimatedCheckmark = () => {
  return (
    <Box
      position="relative"
      width="fit-content"
      height="fit-content"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Circle
        size="48px"
        bg={HelivanColor.hover.lightBlue}
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        overflow="hidden"
        className="pulse-animation"
      >
        <Box
          as="div"
          className="checkmark"
          position="relative"
          transform="rotate(45deg)"
          width="16px"
          height="25px"
          borderBottom="6px solid"
          borderRight="6px solid"
          borderColor={HelivanColor.text.blueDark}
          marginBottom="0px"
        />
      </Circle>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .checkmark {
            animation: checkmark-appear 0.8s ease-in-out forwards;
            opacity: 0;
          }
          
          .pulse-animation {
            animation: pulse 2s infinite;
          }
          
          @keyframes checkmark-appear {
            0% {
              opacity: 0;
              transform: rotate(45deg) scale(0.5);
            }
            50% {
              opacity: 1;
              transform: rotate(45deg) scale(1.2);
            }
            100% {
              opacity: 1;
              transform: rotate(45deg) scale(1);
            }
          }
          
          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(17, 68, 113, 0.4);
            }
            70% {
              box-shadow: 0 0 0 10px rgba(17, 68, 113, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(17, 68, 113, 0);
            }
          }
        `,
        }}
      />
    </Box>
  );
};
